import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Users,
  Trophy,
  TrendingUp,
  Activity,
  Settings,
  LogOut,
  ChevronRight,
  Target,
} from 'lucide-react';

export function DashboardPage() {
  const { user, organization, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Mock statistics data
  const stats = {
    totalSessions: 47,
    averageScore: 78.5,
    improvement: 12.3,
    activePlayers: 8,
  };

  const recentSessions = [
    { id: 1, player: 'John Smith', date: '2024-01-18', score: 82, shots: 120 },
    { id: 2, player: 'Sarah Johnson', date: '2024-01-18', score: 76, shots: 95 },
    { id: 3, player: 'Mike Chen', date: '2024-01-17', score: 89, shots: 110 },
    { id: 4, player: 'Emma Wilson', date: '2024-01-17', score: 71, shots: 88 },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Trophy className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">SixSight Dashboard</h1>
                {organization && (
                  <p className="text-sm text-muted-foreground">{organization.name}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-muted-foreground">
                Welcome, {user?.name || user?.email}
              </span>
              <Button variant="ghost" size="icon">
                <Settings className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Welcome back, {user?.name}!</h2>
          <p className="text-muted-foreground">
            Here's your cricket performance overview for today
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid gap-4 md:grid-cols-3 mb-8">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/shot-visualization')}>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>3D Shot Visualization</CardTitle>
                <CardDescription>View real-time 3D shot analysis</CardDescription>
              </div>
              <Target className="h-8 w-8 text-primary" />
            </CardHeader>
            <CardContent>
              <Button variant="ghost" className="p-0">
                View Visualization <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin')}>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Admin Dashboard</CardTitle>
                <CardDescription>Manage users and settings</CardDescription>
              </div>
              <Settings className="h-8 w-8 text-primary" />
            </CardHeader>
            <CardContent>
              <Button variant="ghost" className="p-0">
                Go to Admin <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/sessions')}>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Training Sessions</CardTitle>
                <CardDescription>View all training sessions</CardDescription>
              </div>
              <Activity className="h-8 w-8 text-primary" />
            </CardHeader>
            <CardContent>
              <Button variant="ghost" className="p-0">
                View Sessions <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Sessions</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalSessions}</div>
              <p className="text-xs text-muted-foreground">+20.1% from last month</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Average Score</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.averageScore}%</div>
              <p className="text-xs text-muted-foreground">+5.2% from last week</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Improvement Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">+{stats.improvement}%</div>
              <p className="text-xs text-muted-foreground">Technique accuracy</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Players</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activePlayers}</div>
              <p className="text-xs text-muted-foreground">In your organization</p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Sessions & Quick Actions */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* Recent Sessions */}
          <Card className="col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Recent Training Sessions</CardTitle>
                <CardDescription>Latest performance data from your players</CardDescription>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/sessions')}
              >
                View All
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentSessions.map((session) => (
                  <div
                    key={session.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent cursor-pointer transition-colors"
                    onClick={() => navigate('/sessions')}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-semibold text-primary">
                          {session.player.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{session.player}</p>
                        <p className="text-sm text-muted-foreground">
                          {session.date} • {session.shots} shots
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-lg font-semibold">{session.score}%</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>Common tasks and shortcuts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => navigate('/admin')}
              >
                <Activity className="mr-2 h-4 w-4" />
                Admin Dashboard
              </Button>
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => navigate('/admin/users')}
                disabled={user?.role !== 'ADMIN'}
              >
                <Users className="mr-2 h-4 w-4" />
                Manage Users
              </Button>
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => navigate('/admin/analytics')}
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                View Analytics
              </Button>
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => navigate('/admin/organization')}
                disabled={user?.role !== 'ADMIN'}
              >
                <Settings className="mr-2 h-4 w-4" />
                Organization Settings
              </Button>
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => navigate('/admin/coach')}
                disabled={user?.role !== 'ADMIN' && user?.role !== 'COACH'}
              >
                <Target className="mr-2 h-4 w-4" />
                Coach Tools
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* User Role Badge */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            Logged in as <span className="font-semibold">{user?.role}</span>
            {organization && (
              <> in <span className="font-semibold">{organization.name}</span></>
            )}
          </p>
        </div>
      </main>
    </div>
  );
}